/** @jsxImportSource @emotion/react */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, LabelGroup } from 'semantic-ui-react';
import { t } from 'i18next';
import style from './auditMultiToggle.style';
import { ModalPage } from '../../../shared/ModalPage';
import { FilterBadge } from '../../../shared/FilterBadge';
import { getPriorities, Priority } from '../model/assessmentDetail.model';
import { ToggleField } from './ToggleField';
import { SelectItem } from '../../../shared/ModalSelectField';
import { ModalMultiSelectField } from '../../../shared/ModalMultiSelectField';

interface AssessmentFieldProps {
  editable: boolean;
  defaultFindingsValue: boolean;
  defaultFollowUpValue?: boolean;
  defaultFollowUpToFillInValue?: boolean;
  defaultPendingActionPlanValue?: boolean;
  defaultPendingFollowUpValue?: boolean;
  defaultPriorityFilter: Priority[];
  showFindings: boolean;
  showFollowUp?: boolean;
  showFollowUpToFillIn?: boolean;
  showPendingActionPlan?: boolean;
  showPendingFollowUp?: boolean;
  showPriorityFilter?: boolean;
  onFindingsChange?: (value: boolean) => void;
  onFollowUpChange?: (value: boolean) => void;
  onFollowUpToFillInChange?: (value: boolean) => void;
  onPendingActionPlanChange?: (value: boolean) => void;
  onPendingFollowUpChange?: (value: boolean) => void;
  onPriorityFilterChange?: (value: Priority[]) => void;
}

export const AuditMultiToggle = ({
  editable = false,
  defaultFindingsValue,
  defaultFollowUpValue = false,
  defaultFollowUpToFillInValue = false,
  defaultPendingActionPlanValue = false,
  defaultPendingFollowUpValue = false,
  onFindingsChange,
  onFollowUpChange,
  onFollowUpToFillInChange,
  onPendingActionPlanChange,
  onPendingFollowUpChange,
  onPriorityFilterChange,
  defaultPriorityFilter,
  showFindings = true,
  showFollowUp = false,
  showFollowUpToFillIn = false,
  showPendingActionPlan = false,
  showPendingFollowUp = false,
  showPriorityFilter = true,
}: AssessmentFieldProps): JSX.Element => {
  const [subProcessFilterWindowOpen, setSubProcessFilterWindowOpen] = useState(false);

  const [findingsValue, setFindingsValue] = useState(defaultFindingsValue);
  const [followUpValue, setFollowUpValue] = useState(defaultFollowUpValue);
  const [followUpToFillInValue, setFollowUpToFillInValue] = useState(defaultFollowUpToFillInValue);
  const [pendingActionPlanValue, setPendingActionPlanValue] = useState(defaultPendingActionPlanValue);
  const [pendingFollowUpValue, setPendingFollowUpValue] = useState(defaultPendingFollowUpValue);
  const [priorityFilter, setPriorityFilter] = useState<Priority[]>(defaultPriorityFilter);

  useEffect(() => {
    setFindingsValue(defaultFindingsValue);
    setFollowUpValue(defaultFollowUpValue);
    setFollowUpToFillInValue(defaultFollowUpToFillInValue);
    setPriorityFilter(defaultPriorityFilter);
    setPendingActionPlanValue(defaultPendingActionPlanValue);
    setPendingFollowUpValue(defaultPendingFollowUpValue);
  }, [
    defaultFindingsValue,
    defaultFollowUpValue,
    defaultFollowUpToFillInValue,
    defaultPriorityFilter,
    defaultPendingActionPlanValue,
    defaultPendingFollowUpValue,
  ]);

  const onApplyFilters = useCallback(() => {
    if (onFindingsChange != null) {
      onFindingsChange(findingsValue);
    }
    if (onFollowUpChange != null) {
      onFollowUpChange(followUpValue);
    }

    if (onFollowUpToFillInChange != null) {
      onFollowUpToFillInChange(followUpToFillInValue);
    }

    if (onPriorityFilterChange != null) {
      onPriorityFilterChange(priorityFilter);
    }

    if (onPendingActionPlanChange != null) {
      onPendingActionPlanChange(pendingActionPlanValue);
    }

    if (onPendingFollowUpChange != null) {
      onPendingFollowUpChange(pendingFollowUpValue);
    }

    setSubProcessFilterWindowOpen(false);
  }, [
    findingsValue,
    followUpValue,
    followUpToFillInValue,
    pendingActionPlanValue,
    pendingFollowUpValue,
    onFindingsChange,
    onFollowUpChange,
    onFollowUpToFillInChange,
    onPendingActionPlanChange,
    onPendingFollowUpChange,
    onPriorityFilterChange,
    priorityFilter,
  ]);

  const onClearFilters = useCallback(() => {
    if (onFindingsChange != null) {
      onFindingsChange(false);
    }
    if (onFollowUpChange != null && showFollowUp) {
      onFollowUpChange(false);
    }
    if (onFollowUpToFillInChange != null) {
      onFollowUpToFillInChange(false);
    }
    if (onPendingActionPlanChange != null) {
      onPendingActionPlanChange(false);
    }
    if (onPendingFollowUpChange != null) {
      onPendingFollowUpChange(false);
    }
    if (onPriorityFilterChange != null) {
      onPriorityFilterChange([]);
    }
    setSubProcessFilterWindowOpen(false);
  }, [
    onFindingsChange,
    onFollowUpChange,
    showFollowUp,
    onFollowUpToFillInChange,
    onPendingActionPlanChange,
    onPriorityFilterChange,
    onPendingFollowUpChange,
  ]);

  const getAppliedFilters = useCallback(() => {
    const appliedFilters: { value?: any; label: string }[] = [];

    if (defaultFindingsValue && showFindings) {
      appliedFilters.push({ label: t('assessment.audit.findingsAndToDo') });
    }

    if (defaultFollowUpValue && showFollowUp) {
      appliedFilters.push({ label: t('assessment.audit.followUp') });
    }

    if (defaultFollowUpToFillInValue) {
      appliedFilters.push({ label: t('assessment.audit.followUpToFillIn') });
    }

    if (defaultPendingActionPlanValue) {
      appliedFilters.push({ label: t('assessment.audit.pendingActionPlan') });
    }

    if (defaultPendingFollowUpValue) {
      appliedFilters.push({ label: t('assessment.audit.pendingFollowUp') });
    }

    if (defaultPriorityFilter.length > 0) {
      appliedFilters.push({ label: t('assessment.audit.priority'), value: defaultPriorityFilter.join(', ') });
    }

    return appliedFilters;
  }, [
    defaultFindingsValue,
    showFindings,
    defaultFollowUpValue,
    showFollowUp,
    defaultFollowUpToFillInValue,
    defaultPendingActionPlanValue,
    defaultPendingFollowUpValue,
    defaultPriorityFilter,
  ]);

  return (
    <>
      <div css={editable ? style.editableField : style.field}>
        <div css={style.titleContainer} onClick={event => setSubProcessFilterWindowOpen(true)}>
          {getAppliedFilters().length > 0 ? (
            <div css={style.badgeGroup}>
              <LabelGroup circular>
                {getAppliedFilters().map(f => (
                  <FilterBadge value={f.value} label={f.label} />
                ))}
              </LabelGroup>
            </div>
          ) : (
            <div css={style.noAppliedFiltersLabel}>{t('assessment.audit.filters')}</div>
          )}
          <Button css={style.filterButton} compact icon='filter' />
        </div>
      </div>
      {subProcessFilterWindowOpen && (
        <ModalPage onClose={() => setSubProcessFilterWindowOpen(false)} title={t('assessment.audit.filters') || ''}>
          <div css={style.toggleContainer}>
            {showFindings && (
              <ToggleField
                icon='clipboard list'
                defaultValue={findingsValue}
                title={t('assessment.audit.showOnlyFindingsAndToDo') || ''}
                label={t('assessment.audit.showOnlyFindingsAndToDo') || ''}
                onChange={value => {
                  if (onFindingsChange != null && editable) setFindingsValue(value);
                }}
                editable={editable}
              />
            )}
            {showFollowUp && (
              <ToggleField
                icon='clipboard check'
                defaultValue={followUpValue}
                title={t('assessment.audit.showOnlyFollowUp') || ''}
                label={t('assessment.audit.showOnlyFollowUp') || ''}
                onChange={value => {
                  if (onFollowUpChange != null && editable) setFollowUpValue(value);
                }}
                editable={editable}
              />
            )}
            {showFollowUpToFillIn && (
              <ToggleField
                icon='clipboard check'
                defaultValue={followUpToFillInValue}
                title={t('assessment.audit.showOnlyFollowUpToFillIn') || ''}
                label={t('assessment.audit.showOnlyFollowUpToFillIn') || ''}
                onChange={value => {
                  if (onFollowUpToFillInChange != null && editable) setFollowUpToFillInValue(value);
                }}
                editable={editable}
              />
            )}
            {showPendingActionPlan && (
              <ToggleField
                icon='clipboard check'
                defaultValue={pendingActionPlanValue}
                title={t('assessment.audit.showOnlyPendingActionPlan') || ''}
                label={t('assessment.audit.showOnlyPendingActionPlan') || ''}
                onChange={value => {
                  if (onPendingActionPlanChange != null && editable) setPendingActionPlanValue(value);
                }}
                editable={editable}
              />
            )}
            {showPendingFollowUp && (
              <ToggleField
                icon='clipboard check'
                defaultValue={pendingFollowUpValue}
                title={t('assessment.audit.showOnlyPendingFollowUp') || ''}
                label={t('assessment.audit.showOnlyPendingFollowUp') || ''}
                onChange={value => {
                  if (onPendingFollowUpChange != null && editable) setPendingFollowUpValue(value);
                }}
                editable={editable}
              />
            )}
            {showPriorityFilter && (
              <ModalMultiSelectField
                icon='angle double up'
                defaultValue={
                  priorityFilter.map(v => ({
                    code: v,
                    description: v,
                  })) as SelectItem[]
                }
                list={
                  getPriorities().map(v => ({
                    code: v,
                    description: v,
                  })) as SelectItem[]
                }
                title={t('assessment.audit.priority') || ''}
                editable={editable}
                onUpdateValue={(selectedItems: SelectItem[]) => {
                  setPriorityFilter(selectedItems.map(b => b.code as Priority));
                }}
              />
            )}

            <div css={style.filterButtonsContainer}>
              <Button
                css={style.applyFiltersButton}
                content={t('assessment.audit.clearFilters')}
                onClick={onClearFilters}
                icon='eraser'
              />
              <Button
                css={style.applyFiltersButton}
                content={t('assessment.audit.applyFilters')}
                onClick={onApplyFilters}
                icon='filter'
              />
            </div>
          </div>
        </ModalPage>
      )}
    </>
  );
};
